/**
 * SettingsPage Component
 * User and organization settings management
 */

import { BaseComponent } from '../lib/BaseComponent.js';
import { api } from '../lib/api.js';
import { router } from '../lib/router.js';
import { buildAppNav, APP_NAV_STYLES } from '../lib/app-nav.js';
import { Toast } from './shared/Toast.js';

export class SettingsPage extends BaseComponent {
  private user: any = null;
  private organization: any = null;
  private isLoading: boolean = true;
  private isSaving: boolean = false;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadData();
    // Replace the loading state instead of stacking a second copy of the page.
    this.shadow.replaceChildren();
    await this.render();
  }

  private async loadData(): Promise<void> {
    try {
      // Current user from the session token (synchronous), then the authoritative record
      const cached = api.currentUser();
      this.user = cached ? { ...cached, organization: undefined } : null;
      try {
        const me = await api.me();
        this.user = { ...me.user, organization: me.organization };
        this.organization = {
          id: me.organization?.id || '',
          name: me.organization?.name || 'My Organization',
          plan_type: me.organization?.plan_id || me.organization?.subscription_tier || 'trial',
          created_at: me.organization?.created_at ? new Date(Number(me.organization.created_at)).toISOString() : new Date().toISOString()
        };
      } catch {
        this.organization = {
          id: '',
          name: 'My Organization',
          plan_type: 'trial',
          created_at: new Date().toISOString()
        };
      }

      this.isLoading = false;
    } catch (error) {
      console.error('Failed to load settings:', error);
      this.isLoading = false;
    }
  }

  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'page-container' });

    // Header
    const header = this.createHeader();
    container.appendChild(header);

    // Main content
    const main = this.createElement('main', { className: 'settings-main' });

    if (this.isLoading) {
      main.appendChild(this.createElement('loading-spinner'));
    } else {
      // Page title
      const pageTitle = this.createElement('div', { className: 'page-title' });
      pageTitle.appendChild(this.createElement('h1', { textContent: 'Settings' }));
      pageTitle.appendChild(this.createElement('p', {
        className: 'subtitle',
        textContent: 'Manage your account and organization preferences'
      }));
      main.appendChild(pageTitle);

      // Settings sections
      const sections = this.createElement('div', { className: 'settings-sections' });

      // User Profile Section
      sections.appendChild(this.createProfileSection());

      // Organization Section
      sections.appendChild(this.createOrganizationSection());

      // Notification Preferences
      sections.appendChild(this.createNotificationsSection());

      // Security Section
      sections.appendChild(this.createSecuritySection());
      if ((this.user?.role || api.currentUser()?.role) === 'admin') sections.appendChild(this.createDangerZoneSection());

      main.appendChild(sections);
    }

    container.appendChild(main);
    this.shadow.appendChild(container);
  }

  private createHeader(): HTMLElement {
    const header = this.createElement('header', { className: 'page-header' });
    const headerContent = this.createElement('div', { className: 'header-content' });

    const logo = this.createElement('h1', {
      className: 'logo',
      textContent: 'FireGuard'
    });
    logo.style.cursor = 'pointer';
    logo.addEventListener('click', () => router.push('/dashboard'));

    const nav = buildAppNav((t, p, txt) => this.createElement(t as any, p as any, txt as any), '/settings', () => this.handleLogout());
    headerContent.appendChild(nav);
    header.appendChild(headerContent);

    return header;
  }

  private createProfileSection(): HTMLElement {
    const section = this.createElement('section', { className: 'settings-section' });

    section.appendChild(this.createElement('h2', { textContent: 'User Profile' }));
    section.appendChild(this.createElement('p', {
      className: 'section-description',
      textContent: 'Update your personal information and preferences'
    }));

    const form = this.createElement('form', { className: 'settings-form' });
    form.addEventListener('submit', (e) => this.handleProfileUpdate(e));

    // Name field
    const nameGroup = this.createFormGroup('Name', 'name', 'text', this.user?.name || '');
    form.appendChild(nameGroup);

    // Email field (read-only)
    const emailGroup = this.createFormGroup('Email', 'email', 'email', this.user?.email || '', true);
    form.appendChild(emailGroup);

    // Role display
    const roleGroup = this.createFormGroup('Role', 'role', 'text', this.user?.role || 'admin', true);
    form.appendChild(roleGroup);

    // Save button
    const saveBtn = this.createElement('button', {
      type: 'submit',
      className: 'btn-primary',
      textContent: this.isSaving ? 'Saving...' : 'Save Changes'
    }) as HTMLButtonElement;
    saveBtn.disabled = this.isSaving;
    form.appendChild(saveBtn);

    section.appendChild(form);
    return section;
  }

  private createOrganizationSection(): HTMLElement {
    const section = this.createElement('section', { className: 'settings-section' });

    section.appendChild(this.createElement('h2', { textContent: 'Organization' }));
    section.appendChild(this.createElement('p', {
      className: 'section-description',
      textContent: 'Organization information and settings'
    }));

    const isAdmin = (this.user?.role || api.currentUser()?.role) === 'admin';
    const form = this.createElement('form', { className: 'settings-form' });
    form.addEventListener('submit', (e) => this.handleOrganizationUpdate(e));

    // Organization name (admins can rename)
    const nameGroup = this.createFormGroup('Organization Name', 'org_name', 'text', this.organization?.name || '', !isAdmin);
    form.appendChild(nameGroup);

    // Plan: link to the billing page, which is the source of truth
    const planGroup = this.createElement('div', { className: 'form-group' });
    planGroup.appendChild(this.createElement('label', { textContent: 'Plan' }));
    const planRow = this.createElement('div', { className: 'plan-row' });
    planRow.appendChild(this.createElement('span', { className: 'plan-name', id: 'plan-name', textContent: 'Loading…' }));
    const planLink = this.createElement('a', { href: '/billing', className: 'plan-link' }, isAdmin ? 'Manage plan & billing →' : 'View plan →');
    planLink.addEventListener('click', (e) => {
      e.preventDefault();
      router.navigate('/billing');
    });
    planRow.appendChild(planLink);
    planGroup.appendChild(planRow);
    form.appendChild(planGroup);
    api.getBilling()
      .then((b) => {
        const ent = b.entitlements;
        const el = this.shadow.getElementById('plan-name');
        if (el) el.textContent = ent.isTrial ? `${ent.planName} · ${ent.trialDaysLeft ?? 0} days left` : `${ent.planName} · ${ent.status.replace('_', ' ')}`;
      })
      .catch(() => {
        const el = this.shadow.getElementById('plan-name');
        if (el) el.textContent = 'Unavailable';
      });

    // Member since
    const memberSince = this.organization?.created_at
      ? new Date(this.organization.created_at).toLocaleDateString()
      : 'Unknown';
    const sinceGroup = this.createFormGroup('Member Since', 'member_since', 'text', memberSince, true);
    form.appendChild(sinceGroup);

    if (isAdmin) {
      const saveBtn = this.createElement('button', { type: 'submit', className: 'btn-primary', textContent: 'Save organization' });
      form.appendChild(saveBtn);
    }

    section.appendChild(form);
    return section;
  }

  private createNotificationsSection(): HTMLElement {
    const section = this.createElement('section', { className: 'settings-section' });
    section.appendChild(this.createElement('h2', { textContent: 'Notification Preferences' }));
    section.appendChild(this.createElement('p', {
      className: 'section-description',
      textContent: 'Daily digest, weekly summary, monthly report, quiet hours and time zone are managed on the notifications page.'
    }));
    const link = this.createElement('a', { href: '/notifications', className: 'plan-link' }, 'Open notification settings →');
    link.addEventListener('click', (e) => {
      e.preventDefault();
      router.navigate('/notifications');
    });
    section.appendChild(link);
    return section;
  }

  private createSecuritySection(): HTMLElement {
    const section = this.createElement('section', { className: 'settings-section security-section' });

    section.appendChild(this.createElement('h2', { textContent: 'Security' }));
    section.appendChild(this.createElement('p', {
      className: 'section-description',
      textContent: 'Manage your account security settings'
    }));

    // Change password button
    const changePasswordBtn = this.createElement('button', {
      className: 'btn-secondary',
      textContent: '🔒 Change Password'
    });
    const pwForm = this.createPasswordForm();
    pwForm.style.display = 'none';
    changePasswordBtn.addEventListener('click', () => {
      pwForm.style.display = pwForm.style.display === 'none' ? 'grid' : 'none';
    });
    section.appendChild(changePasswordBtn);
    section.appendChild(pwForm);

    // Session info
    const sessionInfo = this.createElement('div', { className: 'session-info' });
    sessionInfo.appendChild(this.createElement('h3', { textContent: 'Active Session' }));
    sessionInfo.appendChild(this.createElement('p', {
      textContent: `You are currently logged in as ${this.user?.email}`
    }));

    const logoutAllBtn = this.createElement('button', {
      className: 'btn-danger',
      textContent: 'Logout from All Devices'
    });
    logoutAllBtn.addEventListener('click', () => {
      if (confirm('This will log you out from all devices. Continue?')) {
        this.handleLogout();
      }
    });
    sessionInfo.appendChild(logoutAllBtn);

    section.appendChild(sessionInfo);

    return section;
  }

  private createPasswordForm(): HTMLFormElement {
    const form = this.createElement('form', { className: 'password-form' });
    form.style.gap = '12px';
    form.style.marginTop = '12px';
    form.style.maxWidth = '420px';
    const fields: Array<[string, string, string]> = [
      ['currentPassword', 'Current password', 'current-password'],
      ['newPassword', 'New password', 'new-password'],
      ['confirmPassword', 'Confirm new password', 'new-password']
    ];
    for (const [name, label, autocomplete] of fields) {
      const group = this.createElement('div', { className: 'form-group' });
      group.appendChild(this.createElement('label', { for: `pw-${name}`, textContent: label }));
      group.appendChild(this.createElement('input', { type: 'password', id: `pw-${name}`, name, required: 'true', autocomplete, minlength: name === 'currentPassword' ? '1' : '8' }));
      form.appendChild(group);
    }
    const submit = this.createElement('button', { type: 'submit', className: 'btn-primary', textContent: 'Update password' });
    form.appendChild(submit);
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const data = new FormData(form);
      const current = (data.get('currentPassword') as string) || '';
      const next = (data.get('newPassword') as string) || '';
      const confirm = (data.get('confirmPassword') as string) || '';
      if (next !== confirm) {
        this.showToast('New passwords do not match', 'error');
        return;
      }
      submit.setAttribute('disabled', 'true');
      try {
        await api.changePassword(current, next);
        this.showToast('Password updated', 'success');
        form.reset();
        form.style.display = 'none';
      } catch (error: any) {
        this.showToast(error?.message || 'Could not change password', 'error');
      } finally {
        submit.removeAttribute('disabled');
      }
    });
    return form;
  }

  private createFormGroup(label: string, name: string, type: string, value: string, readonly: boolean = false): HTMLElement {
    const group = this.createElement('div', { className: 'form-group' });

    const labelEl = this.createElement('label', {
      htmlFor: name,
      textContent: label
    });

    const input = this.createElement('input', {
      type: type,
      name: name,
      id: name,
      value: value,
      readOnly: readonly
    }) as HTMLInputElement;

    if (readonly) {
      input.style.backgroundColor = 'var(--bg-secondary)';
      input.style.cursor = 'not-allowed';
    }

    group.appendChild(labelEl);
    group.appendChild(input);

    return group;
  }

  private createCheckboxGroup(label: string, name: string, description: string, checked: boolean, disabled: boolean = false): HTMLElement {
    const group = this.createElement('div', { className: 'checkbox-group' });

    const labelWrapper = this.createElement('div', { className: 'checkbox-label-wrapper' });

    const checkbox = this.createElement('input', {
      type: 'checkbox',
      name: name,
      id: name,
      checked: checked,
      disabled: disabled
    }) as HTMLInputElement;

    const labelEl = this.createElement('label', {
      htmlFor: name
    });

    const labelText = this.createElement('div', {
      className: 'checkbox-label',
      textContent: label
    });

    const labelDesc = this.createElement('div', {
      className: 'checkbox-description',
      textContent: description
    });

    labelEl.appendChild(labelText);
    labelEl.appendChild(labelDesc);

    labelWrapper.appendChild(checkbox);
    labelWrapper.appendChild(labelEl);
    group.appendChild(labelWrapper);

    return group;
  }

  private async handleProfileUpdate(event: Event): Promise<void> {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const name = (formData.get('name') as string || '').trim();
    if (!name) {
      this.showToast('Please enter your name', 'error');
      return;
    }

    this.isSaving = true;
    this.shadow.replaceChildren();
    await this.render();

    try {
      const res = await api.put('/api/auth/me', { name });
      if (this.user) this.user.name = res?.user?.name || name;
      try {
        const stored = JSON.parse(localStorage.getItem('user') || 'null');
        if (stored) localStorage.setItem('user', JSON.stringify({ ...stored, name: this.user?.name || name }));
      } catch { /* ignore */ }
      this.showToast('Profile updated', 'success');
    } catch (error) {
      console.error('Failed to update profile:', error);
      this.showToast((error as Error).message || 'Failed to update profile', 'error');
    } finally {
      this.isSaving = false;
      this.shadow.replaceChildren();
      await this.render();
    }
  }

  private createDangerZoneSection(): HTMLElement {
    const section = this.createElement('section', { className: 'settings-section danger-zone' });
    section.appendChild(this.createElement('h2', { textContent: 'Delete organization' }));
    section.appendChild(this.createElement('p', {
      className: 'section-description',
      textContent: 'Permanently deletes every location, extinguisher, inspection record, photo and team member, and cancels any subscription. Export your records first; this cannot be undone.'
    }));
    const exportLink = this.createElement('a', { href: '/reports', className: 'plan-link' }, 'Export records (CSV / PDF) →');
    exportLink.addEventListener('click', (e) => {
      e.preventDefault();
      router.navigate('/reports');
    });
    section.appendChild(exportLink);

    const form = this.createElement('form', { className: 'settings-form danger-form' });
    form.appendChild(this.createFormGroup(`Type the organization name (${this.organization?.name || ''}) to confirm`, 'confirm_name', 'text', ''));
    form.appendChild(this.createFormGroup('Your password', 'password', 'password', ''));
    const btn = this.createElement('button', { type: 'submit', className: 'btn-danger', textContent: 'Delete organization permanently' });
    form.appendChild(btn);
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const confirmName = String(fd.get('confirm_name') || '').trim();
      const password = String(fd.get('password') || '');
      if (!confirmName || !password) {
        this.showToast('Enter the organization name and your password', 'error');
        return;
      }
      btn.setAttribute('disabled', 'true');
      btn.textContent = 'Deleting…';
      try {
        await api.delete('/api/auth/organization', { confirm_name: confirmName, password });
        api.logout();
        localStorage.removeItem('user');
        window.location.href = '/?deleted=1';
      } catch (error) {
        this.showToast((error as Error).message || 'Could not delete organization', 'error');
        btn.removeAttribute('disabled');
        btn.textContent = 'Delete organization permanently';
      }
    });
    section.appendChild(form);
    return section;
  }

  private async handleOrganizationUpdate(event: Event): Promise<void> {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const name = ((new FormData(form)).get('org_name') as string || '').trim();
    if (name.length < 2) {
      this.showToast('Please enter an organization name', 'error');
      return;
    }
    try {
      await api.put('/api/auth/organization', { name });
      if (this.organization) this.organization.name = name;
      this.showToast('Organization updated', 'success');
    } catch (error) {
      this.showToast((error as Error).message || 'Failed to update organization', 'error');
    }
  }

  private handleLogout(): void {
    api.logout();
    router.push('/login');
  }

  private showToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success'): void {
    // Routes through the registered <app-toast> component; a bare document.createElement
    // of an unregistered tag renders nothing and silently swallows the message.
    if (type === 'error') Toast.error(message);
    else if (type === 'warning') Toast.warning(message);
    else if (type === 'info') Toast.info(message);
    else Toast.success(message);
  }

  private getStyles(): string {
    return `
      ${APP_NAV_STYLES}

      .page-container {
        min-height: 100vh;
        background: var(--bg-primary);
      }

      .page-header {
        background: white;
        border-bottom: 1px solid var(--border-color);
        padding: 16px 24px;
        box-shadow: var(--shadow-sm);
      }

      .header-content {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .logo {
        font-size: 24px;
        font-weight: bold;
        color: var(--primary-color);
        margin: 0;
      }

      .nav {
        display: flex;
        gap: 12px;
        align-items: center;
      }

      .nav-button {
        background: none;
        border: none;
        color: var(--text-primary);
        font-size: 15px;
        cursor: pointer;
        padding: 8px 16px;
        border-radius: 6px;
        transition: all 0.2s;
      }

      .nav-button:hover {
        background: var(--bg-secondary);
      }

      .settings-main {
        max-width: 900px;
        margin: 0 auto;
        padding: 32px 24px;
      }

      .page-title {
        margin-bottom: 32px;
      }

      .page-title h1 {
        font-size: 32px;
        color: var(--text-primary);
        margin: 0 0 8px 0;
      }

      .subtitle {
        color: var(--text-secondary);
        font-size: 16px;
        margin: 0;
      }

      .settings-sections {
        display: flex;
        flex-direction: column;
        gap: 32px;
      }

      .settings-section {
        background: white;
        border-radius: 12px;
        padding: 32px;
        box-shadow: var(--shadow-sm);
      }

      .settings-section h2 {
        font-size: 20px;
        color: var(--text-primary);
        margin: 0 0 8px 0;
      }

      .section-description {
        color: var(--text-secondary);
        font-size: 14px;
        margin: 0 0 24px 0;
      }

      .settings-form {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .form-group label {
        font-size: 14px;
        font-weight: 600;
        color: var(--text-primary);
      }

      .form-group input {
        padding: 12px;
        border: 1px solid var(--border-color);
        border-radius: 8px;
        font-size: 15px;
        font-family: inherit;
        transition: border-color 0.2s;
      }

      .form-group input:focus {
        outline: none;
        border-color: var(--primary-color);
      }

      .checkbox-group {
        padding: 16px;
        background: var(--bg-secondary);
        border-radius: 8px;
      }

      .checkbox-label-wrapper {
        display: flex;
        align-items: flex-start;
        gap: 12px;
      }

      .checkbox-label-wrapper input[type="checkbox"] {
        width: 20px;
        height: 20px;
        cursor: pointer;
        margin-top: 2px;
      }

      .checkbox-label-wrapper label {
        flex: 1;
        cursor: pointer;
      }

      .checkbox-label {
        font-size: 15px;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 4px;
      }

      .checkbox-description {
        font-size: 13px;
        color: var(--text-secondary);
      }

      .btn-primary, .btn-secondary, .btn-danger {
        padding: 12px 24px;
        border-radius: 8px;
        border: none;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-primary {
        background: var(--primary-color);
        color: white;
      }

      .btn-primary:hover:not(:disabled) {
        background: var(--primary-hover);
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
      }

      .btn-primary:disabled {
        background: #9ca3af;
        cursor: not-allowed;
      }

      .btn-secondary {
        background: var(--text-primary);
        color: white;
      }

      .btn-secondary:hover {
        background: #374151;
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
      }

      .btn-danger {
        background: var(--danger-color);
        color: white;
      }

      .btn-danger:hover {
        background: #b91c1c;
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
      }

      .session-info {
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid var(--border-color);
      }

      .session-info h3 {
        font-size: 16px;
        color: var(--text-primary);
        margin: 0 0 8px 0;
      }

      .session-info p {
        color: var(--text-secondary);
        font-size: 14px;
        margin: 0 0 16px 0;
      }

      .danger-zone { border: 1px solid #fecaca; }
      .danger-zone h2 { color: #b91c1c; }
      .danger-form { margin-top: 16px; }
      .btn-danger { align-self: flex-start; padding: 10px 16px; background: #dc2626; color: #fff; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; }
      .btn-danger:hover:not([disabled]) { background: #b91c1c; }
      .btn-danger[disabled] { opacity: 0.6; cursor: not-allowed; }
      .plan-row { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; }
      .plan-name { font-weight: 600; }
      .plan-link { color: var(--primary, #3b82f6); text-decoration: none; font-size: 14px; }

      @media (max-width: 768px) {
        .settings-main {
          padding: 24px 16px;
        }

        .settings-section {
          padding: 24px 16px;
        }
      }
    `;
  }
}

customElements.define('settings-page', SettingsPage);
